import { Badge, Group, Text } from "@mantine/core";
import { useCallback } from "react";
import { useTranslation } from "react-i18next";

/** minorDigits is how many minor-unit digits a currency has (2 for KES: 100 cents to the shilling). */
export function minorDigits(currency: string): number {
  try {
    return new Intl.NumberFormat("en", { style: "currency", currency }).resolvedOptions().maximumFractionDigits ?? 2;
  } catch {
    return 2;
  }
}

/**
 * formatMoney shows an integer amount in minor units (cents) in the given
 * currency and language, e.g. 123450 KES → "KES 1,234.50". The currency
 * always comes from the API or the brand, never from code.
 */
export function formatMoney(minor: number, currency: string, lng: string): string {
  const digits = minorDigits(currency);
  const major = minor / 10 ** digits;
  try {
    return new Intl.NumberFormat(lng, {
      style: "currency",
      currency,
      currencyDisplay: "code",
      minimumFractionDigits: digits,
      maximumFractionDigits: digits,
    }).format(major);
  } catch {
    return `${currency} ${major.toFixed(digits)}`;
  }
}

/**
 * parseMoney turns a typed amount in major units ("1,234.5") into integer
 * minor units (123450), without floating-point rounding. It returns null for
 * anything that is not a positive amount with at most the currency's decimals.
 */
export function parseMoney(input: string, currency: string): number | null {
  const digits = minorDigits(currency);
  const s = input.replace(/[\s,]/g, "");
  const m = /^(\d+)(?:\.(\d*))?$/.exec(s);
  if (!m) return null;
  const whole = m[1] ?? "0";
  const frac = m[2] ?? "";
  if (frac.length > digits) return null;
  const minor = Number(whole + frac.padEnd(digits, "0"));
  return Number.isSafeInteger(minor) && minor > 0 ? minor : null;
}

/** parsePrice is parseMoney that also accepts zero (a free price). */
export function parsePrice(input: string, currency: string): number | null {
  if (/^\s*0+(\.0*)?\s*$/.test(input)) return 0;
  return parseMoney(input, currency);
}

/** majorUnits shows minor units as a plain amount for an input field, e.g. 150050 → "1500.50". */
export function majorUnits(minor: number, currency: string): string {
  const digits = minorDigits(currency);
  return (minor / 10 ** digits).toFixed(digits);
}

/** useFormat formats timestamps, money and field names in the current language. */
export function useFormat() {
  const { t, i18n } = useTranslation();
  const lng = i18n.language;
  const dateTime = useCallback(
    (iso: string) => new Intl.DateTimeFormat(lng, { dateStyle: "medium", timeStyle: "short" }).format(new Date(iso)),
    [lng],
  );
  const date = useCallback(
    (iso: string) => new Intl.DateTimeFormat(lng, { dateStyle: "medium" }).format(new Date(iso)),
    [lng],
  );
  // A calendar day ("2026-10-05") is not an instant: format it as written.
  const day = useCallback(
    (d: string) =>
      new Intl.DateTimeFormat(lng, { dateStyle: "medium", timeZone: "UTC" }).format(new Date(`${d}T00:00:00Z`)),
    [lng],
  );
  const money = useCallback((minor: number, currency: string) => formatMoney(minor, currency, lng), [lng]);
  const field = useCallback((f: string) => t(`fields.${f}`, { defaultValue: f }), [t]);
  const fields = useCallback(
    (fs: readonly string[]) => (fs.length ? fs.map(field).join(", ") : t("fields.none")),
    [field, t],
  );
  const channel = useCallback((c: string) => t(`channel.${c}`, { defaultValue: c }), [t]);
  return { dateTime, date, day, money, field, fields, channel };
}

/** FieldBadges shows consent fields (email, phone, ID number) as badges. */
export function FieldBadges({ fields, color = "brand" }: { fields: readonly string[]; color?: string }) {
  const f = useFormat();
  if (!fields.length) {
    return (
      <Text size="sm" c="dimmed">
        {f.fields(fields)}
      </Text>
    );
  }
  return (
    <Group gap={4}>
      {fields.map((x) => (
        <Badge key={x} color={color} variant="light">
          {f.field(x)}
        </Badge>
      ))}
    </Group>
  );
}
